/**
 * Commissions — Tricura (15% default) + Agent (10% default) with the
 * computed Total Commission row. Changing these recalculates the
 * effective rates downstream (backend concern — phase 2).
 */
import { PieChart01 } from "@untitledui/icons";
import { FormGroup } from "../../../../components/form-group";
import { PercentInput } from "../../../../components/percent-input";
import { clampPctToWire } from "../premium-selection-types";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, ComputedRow, FieldRow } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import { displayPctNumber } from "../premium-selection-types";
import { FIELD_TOOLTIPS } from "../tooltips";
import type { InputKey, PendingMap } from "../use-premium-selection";

interface CommissionsCardProps {
    inputs: PremiumSelectionInputs;
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
}

export function CommissionsCard({ inputs, derived, pending, setInput, commit }: CommissionsCardProps) {
    return (
        <FormGroup title="Commissions" icon={PieChart01} data-autotest="premium-selection-commissions-card">
            <CardRows>
                <PercentRow
                    inputKey="tricuraCommissionPct"
                    label="Tricura Commission"
                    value={inputs.tricuraCommissionPct}
                    setInput={setInput}
                    commit={commit}
                    dataAutotest="premium-selection-commissions-tricura-commission-row"
                />
                <PercentRow
                    inputKey="agentCommissionPct"
                    label="Agent Commission"
                    value={inputs.agentCommissionPct}
                    setInput={setInput}
                    commit={commit}
                    dataAutotest="premium-selection-commissions-agent-commission-row"
                />
                <ComputedRow
                    label="Total Commission"
                    tooltip={FIELD_TOOLTIPS.totalCommission}
                    value={displayPctNumber(derived?.totalCommissionPct ?? null)}
                    isPending={Boolean(pending.totalCommissionPct) || derived === null}
                    data-autotest="premium-selection-commissions-total-commission-row"
                />
            </CardRows>
        </FormGroup>
    );
}

export function PercentRow({
    inputKey,
    label,
    value,
    setInput,
    commit,
    dataAutotest,
}: {
    inputKey: InputKey;
    label: string;
    value: string;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
    dataAutotest?: string;
}) {
    const { flashKey, flash } = useBlurFlash();
    return (
        <FieldRow label={label} flashKey={flashKey} controlClassName="w-24" data-autotest={dataAutotest}>
            <PercentInput
                aria-label={label}
                value={value}
                onChange={(raw) => setInput(inputKey, raw)}
                onBlur={() => {
                    // Keep the PUT inside the wire's Numeric(8,6) range.
                    const clamped = clampPctToWire(value);
                    if (clamped !== value) setInput(inputKey, clamped);
                    commit(inputKey);
                    flash();
                }}
            />
        </FieldRow>
    );
}
